import assert from 'assert';
import { addDays } from 'date-fns';

import { Id } from '@/fx/Id';

import { CLM } from '@/common/schema/claims';
import { CLP } from '@/common/schema/claims-process';
import { randomBeverageType_enum, randomContact, randomContactName, randomFlcaClaimApproverLevel, randomIntRange, randomUlcaClaimApproverLevels, randomWholesaler } from '@/common/schema/claims-form-helpers';
import { dateOnly_FROM_date, stringifyReplacer } from '@/fx/data-helpers';
import { SYS } from '@/common/schema/system';

export namespace SAMPLE_DATA {

  export const RC_NOVICE      = Id.from(101n) as CLM.UserId;
  export const WCA_NOVICE     = Id.from(102n) as CLM.UserId;
  export const RC_PRO         = Id.from(103n) as CLM.UserId;
  export const RC_EXP         = Id.from(105n) as CLM.UserId;
  export const WCA_EXP        = Id.from(106n) as CLM.UserId;

  export const WCA_PRO        = Id.from(201n) as CLM.UserId;
  export const WCA_AS_PRO     = Id.from(202n) as CLM.UserId;
  export const ARS_PRO        = Id.from(203n) as CLM.UserId;

  export const BBC_APR_NOVICE = Id.from(301n) as CLM.UserId;
  export const DIS_NOVICE     = Id.from(302n) as CLM.UserId;

  export class DataGenerator
  {
    get seedUsers      () { return seedUsers       };
    get seedWholesalers() { return seedWholesalers };
    get seedClaims     () { return seedClaims      };
  }

  function currentUser() {
    return seedUsers.find(_ => _.id === RC_NOVICE)!
  }

// #region Seed Users

  // const seedUsers: CLM.User[] = [];

  const seedUsers: CLM.User[] = [
    { id: RC_NOVICE,      name: "Jill Smith",         settings: { experience: CLM.Experience.NOVICE, persona: CLP.ActorType.RETURNS_COORDINATOR                    }} as CLM.User,
    { id: WCA_NOVICE,     name: "Jack Johnson",       settings: { experience: CLM.Experience.NOVICE, persona: CLP.ActorType.WHOLESALER_CREDIT_ACCOUNTANT           }} as CLM.User,
    { id: RC_PRO,         name: "Michael Roberts",    settings: { experience: CLM.Experience.PRO,    persona: CLP.ActorType.RETURNS_COORDINATOR                    }} as CLM.User,
    { id: WCA_PRO,        name: "Samantha Brown",     settings: { experience: CLM.Experience.PRO,    persona: CLP.ActorType.WHOLESALER_CREDIT_ACCOUNTANT           }} as CLM.User,
    { id: RC_EXP,         name: "John Doe",           settings: { experience: CLM.Experience.PRO,    persona: CLP.ActorType.RETURNS_COORDINATOR                    }} as CLM.User,
    { id: WCA_EXP,        name: "Jane Doe",           settings: { experience: CLM.Experience.PRO,    persona: CLP.ActorType.WHOLESALER_CREDIT_ACCOUNTANT           }} as CLM.User,

    { id: WCA_PRO,        name: "Billy Pro WCA",      settings: { experience: CLM.Experience.PRO,    persona: CLP.ActorType.WHOLESALER_CREDIT_ACCOUNTANT           }} as CLM.User,
    { id: WCA_AS_PRO,     name: "Sally Pro WCA_AS",   settings: { experience: CLM.Experience.PRO,    persona: CLP.ActorType.WHOLESALER_CREDIT_ACCOUNTANT_ASSISTANT }} as CLM.User,
    { id: ARS_PRO,        name: "Alice Pro ARS",      settings: { experience: CLM.Experience.PRO,    persona: CLP.ActorType.ACCOUNTS_RECEIVABLE_SUPERVISOR         }} as CLM.User,

    { id: BBC_APR_NOVICE, name: "Bob Novice BBC_APR", settings: { experience: CLM.Experience.NOVICE, persona: CLP.ActorType.BBC_APPROVER                           }} as CLM.User,
    { id: DIS_NOVICE,     name: "Bran Novice DIS",    settings: { experience: CLM.Experience.NOVICE, persona: CLP.ActorType.DISTRIBUTOR                            }} as CLM.User,
  ];
  // const withExperiments = {
  //   showOverdue:                 true,
  //   hideDraftsWhenEmpty:         true,
  //   dueDateAlert_inBusinessDays: 10,
  // }
  // seedUsers.forEach(_ => _.experimentalSettings = [RC_EXP].includes(_.id) ? withExperiments : undefined);

// #endregion
// #region Seed Wholesalers

  const seedWholesalers: CLM.Wholesaler[] = [
    { id: Id.from(201n) as CLM.WholesalerId, sapId: "456SPI", name:"Spirit Craft Distillery" } as CLM.Wholesaler,
    { id: Id.from(202n) as CLM.WholesalerId, sapId: "789BAR", name:"Barley Brewing"          } as CLM.Wholesaler,
    { id: Id.from(203n) as CLM.WholesalerId, sapId: "123BEE", name:"Beehive Brewery"         } as CLM.Wholesaler,
  ];

// #endregion
// #region Seed Claims

  const seedClaims: CLM.Claim[] = [];

// #endregion

// #region Batch of Claims

  export function claim_stage1(dataContext: () => CLM.DataContext): CLM.Claim[] {
    const dc = dataContext();
    const sampleUserId = Id.from(BigInt(1001001)) as SYS.ActorId; // TODO: Make store this in a seedUsers array, will be used in command tests as well
    const sampleUserName = "Jim Smith";
    const claims = [{
      id:                      Id.from(1n) as CLM.ClaimId,

      ...CLM.claimProcessFields_FOR.ttbApproval.prepareTtbSubmission(),

      claimNumber:             "C24-0000",
      sapOrderNumber:          "60083937",
      beverageType:            CLM.BeverageType.BEER_FMB,
      casesRequested:          807,

      schemaVersion:           CLP.SchemaVersion.V1,
      claimType:               CLM.ClaimType.DESTRUCTION,
      claimReason:             CLM.ClaimReason.OUT_OF_CODE,
      itemized:                false,
      involvesTaxCredit:       true,
      sapOrderTiming:          CLM.SapOrderTiming.AT_CREATION,

      wholesaler_sapId:        "555BTD",
      // wholesaler_nameOverride: "Bean Town Distributing, Inc.",
      wholesaler_nameOverride: "TBD", // This field is used to store stage-testing descriptor JSON, because this is a test claim, part of testing stages, having id == 1

      wholesaler_contactName:  "Alex Alkaline",
      wholesaler_contactEmail: "aalkaline@beantown.com",

      flca_claimApprover_level: CLM.ManagerLevel.AREA_MANAGER.identifier,
      flca_claimApprover_name:  "Sally Sales",
      flca_approvedAt:          new Date("2024-01-01"),

      registeredOn:            new Date("2024-01-01"),
      noi:                     "24-000D",

      skipTtb:                 false,

      sysInfo:                 {
                                  created: { at: dc.now, by: { id: sampleUserId, name: sampleUserName } },
                                  updated: { at: dc.now, by: { id: sampleUserId, name: sampleUserName } },
                                  deleted: undefined,
                               } as SYS.Info,

      extensionOne:            CLM.EXTENSION_ONE_DEFAULTS,

    } as CLM.Claim & CLM.ClaimHelpers].map(
      CLM.claim(CLM.claimCleanupContext(dataContext))
    );

    const claimValidationErrors = claims
      .filter(_ => !claimValidator.isValid(_))
      .map(_ => ({ claim: _, errors: claimValidator.validationErrors(_) }));

    assert(
      claimValidationErrors.length === 0,
      `Errors found in defined Claims with following error codes. ${JSON.stringify(claimValidationErrors, stringifyReplacer, 2)}`
    );

    return claims;
  }
  export function batchOfClaims(dataContext: () => CLM.DataContext): { claims: CLM.Claim[], ulcas: CLM.UpperLevelClaimApproval[] } {

    const now = new Date();
    const today = dateOnly_FROM_date(now) as Date;

    var nextId  = 10001n;
    var nextNoi = 1;

    const sampleUserId   = Id.from(BigInt(1_001_001)) as SYS.ActorId; // TODO: Make store this in a seedUsers array, will be used in command tests as well
    const sampleUserName = "Jim Smith";

    const draft_step1 = () => {
      const wholesaler = randomWholesaler();
      return {
        sapOrderNumber:          undefined! as string, //randomSapOrderNumber(),  => will be filled in later in systemic way to avoid duplicates
        beverageType:            randomBeverageType_enum(),
        casesRequested:          randomIntRange(1, 1000),
        wholesaler_sapId:        wholesaler.sapId,
        wholesaler_nameOverride: wholesaler.name,
      };
    }
    const draft_step2 = () => {
      const contact = randomContact();
      return {
        ...draft_step1(),
        wholesaler_contactName:  contact.name,
        wholesaler_contactEmail: contact.email,
      };
    }
    const draft_step3a = () => ({
      ...draft_step2(),
      flca_claimApprover_level: randomFlcaClaimApproverLevel(),
      flca_claimApprover_name:  randomContactName(),
      flca_approvedAt:          addDays(today, -2),
    })
    const draft_step3b = () => ({
      ...draft_step3a(),
      ulca_claimApprover_levels: randomUlcaClaimApproverLevels(),
    })
    const draft_step4 = () => ({
      ...draft_step3b(),
      claimForm_id:            nextId++,
      claimForm_fileName:      "FILL",
      claimForm_uploadedAt:    addDays(today, -1),
    })
    const draft_finalized = () => ({
      ...draft_step4(),
      ulca_claimApprover_levels: undefined,
    })

    type ClaimWithUlcas = CLM.Claim & { ulcas?: CLM.UpperLevelClaimApproval[] };

    const ulcas_FOR_sendClaimApprovalRequests: CLM.UpperLevelClaimApproval[] = [
      { claimApprover_level: CLM.ManagerLevel.REGIONAL_MANAGER  } as CLM.UpperLevelClaimApproval,
      { claimApprover_level: CLM.ManagerLevel.DIVISION_DIRECTOR } as CLM.UpperLevelClaimApproval,
    ].map((_) => ({ ..._,
      id: _.id ?? Id.from(nextId++) as CLM.ClaimId,
    } as CLM.UpperLevelClaimApproval));

    const ulcas_FOR_waitingForClaimApprovals: CLM.UpperLevelClaimApproval[] = [
      { claimApprover_level: CLM.ManagerLevel.REGIONAL_MANAGER,  approvalDeadline: addDays(today, 10) } as CLM.UpperLevelClaimApproval,
      { claimApprover_level: CLM.ManagerLevel.DIVISION_DIRECTOR, approvalDeadline: addDays(today, 20) } as CLM.UpperLevelClaimApproval,
    ].map((_) => ({ ..._,
      id: _.id ?? Id.from(nextId++) as CLM.ClaimId,
      requestedAt: addDays(today, -20),
      claimApprover_name: randomContactName(),
    } as CLM.UpperLevelClaimApproval));

    const ulcas_FOR_overdueClaimApprovals: CLM.UpperLevelClaimApproval[] = [
      { claimApprover_level: CLM.ManagerLevel.REGIONAL_MANAGER,  approvalDeadline: addDays(today, -10) } as CLM.UpperLevelClaimApproval,
      { claimApprover_level: CLM.ManagerLevel.DIVISION_DIRECTOR, approvalDeadline: addDays(today,  10) } as CLM.UpperLevelClaimApproval,
    ].map((_) => ({ ..._,
      id: _.id ?? Id.from(nextId++) as CLM.ClaimId,
      requestedAt: addDays(today, -20),
      claimApprover_name: randomContactName(),
    } as CLM.UpperLevelClaimApproval));

    const seedClaims = [

      // Claims with Custom wholesaler fields
      { ...CLM.claimProcessFields_FOR.claimCreation.step2(), registeredOn: addDays(today, -11), ...draft_step1(), wholesaler_sapId:        "CUSTOM-123"                     } as CLM.Claim & CLM.ClaimHelpers,
      { ...CLM.claimProcessFields_FOR.claimCreation.step2(), registeredOn: addDays(today, -12), ...draft_step1(), wholesaler_nameOverride: "CUSTOM Wholesale Name Override" } as CLM.Claim & CLM.ClaimHelpers,

      // DRAFT Claims - CLAIM_CREATION
      { ...CLM.claimProcessFields_FOR.claimCreation.step2    (), registeredOn: addDays(today, -21), ...draft_step1 () } as CLM.Claim & CLM.ClaimHelpers,
      { ...CLM.claimProcessFields_FOR.claimCreation.step3    (), registeredOn: addDays(today, -22), ...draft_step2 () } as CLM.Claim & CLM.ClaimHelpers,
      { ...CLM.claimProcessFields_FOR.claimCreation.step4    (), registeredOn: addDays(today, -23), ...draft_step3a() } as CLM.Claim & CLM.ClaimHelpers,
      { ...CLM.claimProcessFields_FOR.claimCreation.step4    (), registeredOn: addDays(today, -24), ...draft_step3b() } as CLM.Claim & CLM.ClaimHelpers,
      { ...CLM.claimProcessFields_FOR.claimCreation.stepFinal(), registeredOn: addDays(today, -25), ...draft_step4 () } as CLM.Claim & CLM.ClaimHelpers,

      // OPEN Claims - CLAIM_APPROVAL
      { ...CLM.claimProcessFields_FOR.claimApproval.sendClaimApprovalRequests(                                      ), registeredOn: addDays(today, -31), ...draft_finalized(), ulcas: ulcas_FOR_sendClaimApprovalRequests } as ClaimWithUlcas & CLM.ClaimHelpers,
      { ...CLM.claimProcessFields_FOR.claimApproval.waitingForClaimApprovals ({ firstDeadline: addDays(today,  10) }), registeredOn: addDays(today, -32), ...draft_finalized(), ulcas: ulcas_FOR_waitingForClaimApprovals  } as ClaimWithUlcas & CLM.ClaimHelpers,
      { ...CLM.claimProcessFields_FOR.claimApproval.waitingForClaimApprovals ({ firstDeadline: addDays(today, -10) }), registeredOn: addDays(today, -33), ...draft_finalized(), ulcas: ulcas_FOR_overdueClaimApprovals     } as ClaimWithUlcas & CLM.ClaimHelpers,
      { ...CLM.claimProcessFields_FOR.claimApproval.confirmClaimApprovals    (                                      ), registeredOn: addDays(today, -34), ...draft_finalized(), beverageType: CLM.BeverageType.BEER_FMB    } as CLM.Claim      & CLM.ClaimHelpers,
      { ...CLM.claimProcessFields_FOR.claimApproval.confirmClaimApprovals    (                                      ), registeredOn: addDays(today, -35), ...draft_finalized(), beverageType: CLM.BeverageType.CIDER       } as CLM.Claim      & CLM.ClaimHelpers,

      // OPEN Claims - TTB_APPROVAL
      { ...CLM.claimProcessFields_FOR.ttbApproval.prepareTtbSubmission  (),                                                          registeredOn: addDays(today, -41), ...draft_finalized(), beverageType: CLM.BeverageType.BEER_FMB } as CLM.Claim & CLM.ClaimHelpers,
      { ...CLM.claimProcessFields_FOR.ttbApproval.sendTtbSubmission     (),                                             noi: "FILL", registeredOn: addDays(today, -42), ...draft_finalized(), beverageType: CLM.BeverageType.BEER_FMB } as CLM.Claim & CLM.ClaimHelpers,
      { ...CLM.claimProcessFields_FOR.ttbApproval.waitingForTtbApprovals({ ttbApprovalDeadline: addDays(today,   1) }), noi: "FILL", registeredOn: addDays(today, -43), ...draft_finalized(), beverageType: CLM.BeverageType.BEER_FMB } as CLM.Claim & CLM.ClaimHelpers,
      { ...CLM.claimProcessFields_FOR.ttbApproval.waitingForTtbApprovals({ ttbApprovalDeadline: addDays(today, -20) }), noi: "FILL", registeredOn: addDays(today, -44), ...draft_finalized(), beverageType: CLM.BeverageType.BEER_FMB } as CLM.Claim & CLM.ClaimHelpers,
      { ...CLM.claimProcessFields_FOR.ttbApproval.confirmTtbApprovals   (),                                             noi: "FILL", registeredOn: addDays(today, -45), ...draft_finalized(), beverageType: CLM.BeverageType.BEER_FMB } as CLM.Claim & CLM.ClaimHelpers,

      // OPEN Claims - DESTRUCTION
      { ...CLM.claimProcessFields_FOR.destruction.sendApprovalToDestroy      (), noi: "FILL", registeredOn: addDays(today, -51), ...draft_finalized() } as CLM.Claim & CLM.ClaimHelpers,
      { ...CLM.claimProcessFields_FOR.destruction.confirmDestructionApprovals(), noi: "FILL", registeredOn: addDays(today, -52), ...draft_finalized() } as CLM.Claim & CLM.ClaimHelpers,
      { ...CLM.claimProcessFields_FOR.destruction.waitingForAods             (), noi: "FILL", registeredOn: addDays(today, -53), ...draft_finalized() } as CLM.Claim & CLM.ClaimHelpers,
      { ...CLM.claimProcessFields_FOR.destruction.confirmDestruction         (), noi: "FILL", registeredOn: addDays(today, -54), ...draft_finalized() } as CLM.Claim & CLM.ClaimHelpers,

      // OPEN Claims - ACCOUNTING_APPROVAL
      { ...CLM.claimProcessFields_FOR.wholesalerCreditApproval.recordCreditTotals   (), noi: "FILL", registeredOn: addDays(today, -61), ...draft_finalized() } as CLM.Claim & CLM.ClaimHelpers,
      { ...CLM.claimProcessFields_FOR.wholesalerCreditApproval.approveClaimForCredit(), noi: "FILL", registeredOn: addDays(today, -62), ...draft_finalized() } as CLM.Claim & CLM.ClaimHelpers,

      // CLOSED Claims - ARCHIVED
      { ...CLM.claimProcessFields_FOR.archived.wholesalerCredited(),                                                     noi: "FILL", registeredOn: addDays(today, -81), ...draft_finalized() } as CLM.Claim,
      { ...CLM.claimProcessFields_FOR.archived.canceled({ cancelationReason: "Wholesaler canceled the claim."                 }), noi: "FILL", registeredOn: addDays(today, -82), ...draft_finalized() } as CLM.Claim,
      { ...CLM.claimProcessFields_FOR.archived.denied({ denialReason:        "Claim was denied by the accounting department." }), noi: "FILL", registeredOn: addDays(today, -83), ...draft_finalized() } as CLM.Claim,

    ] as (CLM.Claim & CLM.ClaimHelpers)[];

    // repeat seedClaims 100 times
    const REPEAT_COUNT = 1;
    const multiSeedClaims = Array(REPEAT_COUNT).fill(seedClaims).flat();

    const claimAndUlcas: ClaimWithUlcas[] = (multiSeedClaims).sort((a, b) => a.registeredOn.getTime() - b.registeredOn.getTime()).map((_, index) => ({ ..._,

      id: _.id ?? Id.from(nextId++) as CLM.ClaimId,

      schemaVersion:    _.schemaVersion ?? CLP.SchemaVersion.V1,

      claimType:         _.claimType         ?? CLM.ClaimType.DESTRUCTION,
      claimReason:       _.claimReason       ?? CLM.ClaimReason.OUT_OF_CODE,
      itemized:          _.itemized          ?? false,
      involvesTaxCredit: _.involvesTaxCredit ?? true,
      sapOrderTiming:    _.sapOrderTiming    ?? CLM.SapOrderTiming.AT_CREATION,

      claimNumber:     _.claimNumber    ?? `C23-${(index + 1).toString().padStart(4, "0")}`,
      sapOrderNumber:  _.sapOrderNumber ?? (60082250 + index * 17).toString(),
      beverageType:    _.beverageType   ?? randomBeverageType_enum(),
      casesRequested:  _.casesRequested ?? randomIntRange(50, 1000),

      wholesaler_sapId: _.wholesaler_sapId ?? seedWholesalers[randomIntRange(0, seedWholesalers.length - 1)].sapId,

      // TODO: Delete this after moving all test date to new process for creation of test data
      stage: _.stage ?? _.phase?.stage ?? _.deliverable?.phase.stage,
      phase:            _.phase        ?? _.deliverable?.phase,
      deliverable:                        _.deliverable,

      // TODO: Delete this after moving all test date to new process for creation of test data
      baseline_action: _.baseline_action ?? (
        _.assigned_action && CLP.isOverdueCorrectionAction(_.assigned_action) ? _.assigned_action.for : undefined
      ),

      skipTtb: _.skipTtb ?? false,

      sysInfo: {
                created: { at: _.registeredOn, by: { id: sampleUserId, name: sampleUserName } },
                updated: { at: _.registeredOn, by: { id: sampleUserId, name: sampleUserName } },
                deleted: undefined,
              } as SYS.Info,

      // TODO: Remove this when epic Destruction Affirmation is done
      extensionOne: CLM.EXTENSION_ONE_DEFAULTS,

    } as CLM.Claim & CLM.ClaimHelpers)).map((_) => ({ ... _,

      noi: _.noi === "FILL" ? (
        _.beverageType === CLM.BeverageType.BEER_FMB ? `23-${(nextNoi++).toString().padStart(3, "0")}D` : undefined
      ): _.noi,

      claimForm_fileName: _.claimForm_fileName === "FILL" ? `Claim Form ${_.claimNumber}.pdf` : _.claimForm_fileName,

      baseline_persona : _.baseline_persona ?? (
        _.baseline_action && CLP.action_assignedTo(_.baseline_action)
      ),
      assigned_persona : _.assigned_persona ?? (
        _.assigned_action && CLP.action_assignedTo(_.assigned_action)
      ),

    } as CLM.Claim & CLM.ClaimHelpers));

    const claims = claimAndUlcas.map(
      CLM.claim(CLM.claimCleanupContext(dataContext))
    );
    const ulcas = claimAndUlcas.flatMap(
      _ => (_.ulcas ?? []).map( ulca => ({
        ...ulca,
        claimId: _.id,
      } as CLM.UpperLevelClaimApproval))
    ).map(_ => ({ ..._, id: Id.from(nextId++) as CLM.UpperLevelClaimApprovalId }));

    const claimValidationErrors = claims
      .filter(_ => !claimValidator.isValid(_))
      .map(_ => ({ claim: _, errors: claimValidator.validationErrors(_) }));

    assert(
      claimValidationErrors.length === 0,
      `Errors found in defined Claims with following error codes. ${JSON.stringify(claimValidationErrors, stringifyReplacer, 2)}`
    );

    const ulcaValidationErrors = ulcas
      .filter(_ => !CLM.ulcaValidator.isValid(_))
      .map(_ => ({ ulca: _, errors: CLM.ulcaValidator.validationErrors(_) }));

    assert(
      ulcaValidationErrors.length === 0,
      `Errors found in defined ULCAs with following error codes. ${JSON.stringify(ulcaValidationErrors, stringifyReplacer, 2)}`
    );

    const claimIds = claims.map(_ => _.id.asInt);
    const ulcaIds  = ulcas .map(_ => _.id.asInt);
    const allIds = [...claimIds, ...ulcaIds];
    const duplicateIds = allIds.filter((id, index) => allIds.indexOf(id) !== index);
    assert(
      duplicateIds.length === 0,
      `Duplicate IDs found in defined Claims and ULCAs. ${JSON.stringify(duplicateIds, stringifyReplacer, 2)}`
    );

    return { claims, ulcas };
  }

  const claimValidator = CLM.claimValidator({ currentUser });

// #endregion
}
